import {
    type KeyboardEvent,
    type MouseEvent,
    type PointerEvent,
    type RefObject,
    useEffect,
    useRef,
} from 'react';
import {
    clampedPanelPosition,
    type PanelPosition,
    panelPositionAfterKey,
    type Size,
} from '~/components/toolbar/shared/panelPlacement';
import { isWithinClickSlop } from '~/containers/viewer/viewport/clickSlop';

/** The listeners a panel's drag handle spreads onto itself. */
export interface PanelDragListeners {
    onMouseDown: (event: MouseEvent<HTMLElement>) => void;
    onPointerDown: (event: PointerEvent<HTMLElement>) => void;
    onPointerMove: (event: PointerEvent<HTMLElement>) => void;
    onPointerUp: () => void;
    onPointerCancel: () => void;
    onLostPointerCapture: () => void;
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}

/** The size of the window's layout viewport, in CSS pixels. */
function windowSize(): Size {
    return { width: window.innerWidth, height: window.innerHeight };
}

/** The size of `panel` as laid out, in CSS pixels. */
function panelSize(panel: HTMLElement): Size {
    return { width: panel.offsetWidth, height: panel.offsetHeight };
}

/**
 * Moves a panel in the window by its drag handle. A press that travels past
 * a click drags it, and the arrow keys step it while the handle has the
 * focus (Shift for larger steps). Each move is kept inside the window and
 * handed to `onMove` as the panel's new top-left corner, in window pixels.
 *
 * A mouse press on the handle does not focus it, as the handle takes the
 * arrow keys, and it ends editing inside the panel, so the arrow keys do
 * not change a value there either.
 */
export function usePanelDrag(
    panelRef: RefObject<HTMLElement | null>,
    onMove: (position: PanelPosition) => void
): PanelDragListeners {
    // A press on the handle, and whether it has travelled past a click:
    // until then it leaves the panel where it is.
    const drag = useRef<{
        offsetX: number;
        offsetY: number;
        start: [number, number];
        moved: boolean;
    } | null>(null);
    const end = () => {
        drag.current = null;
    };

    return {
        onMouseDown: (event) => {
            event.preventDefault();
            const active = document.activeElement;
            if (
                active instanceof HTMLElement &&
                panelRef.current?.contains(active)
            ) {
                active.blur();
            }
        },
        onPointerDown: (event) => {
            const panel = panelRef.current;
            if (!panel || event.button !== 0) {
                return;
            }
            const rect = panel.getBoundingClientRect();
            drag.current = {
                offsetX: event.clientX - rect.left,
                offsetY: event.clientY - rect.top,
                start: [event.clientX, event.clientY],
                moved: false,
            };
            event.currentTarget.setPointerCapture(event.pointerId);
        },
        onPointerMove: (event) => {
            const panel = panelRef.current;
            if (!drag.current || !panel) {
                return;
            }
            if ((event.buttons & 1) === 0) {
                end();
                return;
            }
            if (!drag.current.moved) {
                if (
                    isWithinClickSlop(drag.current.start, [
                        event.clientX,
                        event.clientY,
                    ])
                ) {
                    return;
                }
                drag.current.moved = true;
            }
            onMove(
                clampedPanelPosition(
                    {
                        x: event.clientX - drag.current.offsetX,
                        y: event.clientY - drag.current.offsetY,
                    },
                    windowSize(),
                    panelSize(panel)
                )
            );
        },
        onPointerUp: end,
        onPointerCancel: end,
        onLostPointerCapture: end,
        onKeyDown: (event) => {
            const panel = panelRef.current;
            if (!panel) {
                return;
            }
            const rect = panel.getBoundingClientRect();
            const position = panelPositionAfterKey(
                event.key,
                event.shiftKey,
                { x: rect.left, y: rect.top },
                windowSize(),
                panelSize(panel)
            );
            if (!position) {
                return;
            }
            // The arrow keys also step through slices; here they only move
            // the panel.
            event.preventDefault();
            event.stopPropagation();
            onMove(position);
        },
    };
}

/**
 * Keeps a panel at `position` inside the window as the window and the
 * panel resize, handing `onMove` the position that does. Does nothing while
 * `position` is null.
 */
export function useKeptInWindow(
    panelRef: RefObject<HTMLElement | null>,
    position: PanelPosition | null,
    onMove: (position: PanelPosition) => void
): void {
    const latest = useRef({ position, onMove });
    latest.current = { position, onMove };
    const placed = position !== null;

    useEffect(() => {
        const panel = panelRef.current;
        if (!placed || !panel) {
            return undefined;
        }
        const keep = () => {
            const { position: at, onMove: move } = latest.current;
            if (!at) {
                return;
            }
            const kept = clampedPanelPosition(
                at,
                windowSize(),
                panelSize(panel)
            );
            if (kept.x !== at.x || kept.y !== at.y) {
                move(kept);
            }
        };
        keep();
        const resizes = new ResizeObserver(keep);
        resizes.observe(panel);
        window.addEventListener('resize', keep);
        return () => {
            resizes.disconnect();
            window.removeEventListener('resize', keep);
        };
    }, [panelRef, placed]);
}
